import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { usePageTitle } from "@/lib/usePageTitle";
import { useApp } from "@/state/AppContext";
import { api } from "@/lib/api";
import { localized } from "@/theme/tenantTheme";
import { BusinessCard } from "@/components/BusinessCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CAPABILITY_FILTERS = [
  { key: "ordering", labelKey: "directory.filterOrdering", match: (b) => b.capabilities?.includes("ordering") },
  { key: "appointments", labelKey: "directory.filterAppointments", match: (b) => b.capabilities?.includes("appointments") },
  { key: "table_booking", labelKey: "directory.filterTable", match: (b) => b.capabilities?.includes("table_booking") },
  {
    key: "stay_venue",
    labelKey: "directory.filterStay",
    match: (b) => b.capabilities?.includes("stay_booking") || b.capabilities?.includes("venue_booking"),
  },
];

export default function Directory() {
  const { t, lang, tenant, getCategories } = useApp();
  const [params, setParams] = useSearchParams();
  usePageTitle(t("directory.title"));
  const [categories, setCategories] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [offers, setOffers] = useState([]);
  const [query, setQuery] = useState(params.get("q") || "");
  // L1-004: the City Home rail's house "Book a doctor" card (CityAdSlot) links here with
  // `?filter=appointments` so it lands pre-filtered to appointment-taking clinics, rather than
  // needing a category id (which varies per tenant) — reads once, on mount, same as `q`/`sort`
  // above; the chip row itself stays client-only state afterwards, same as before this change.
  const [activeCapabilities, setActiveCapabilities] = useState(() => {
    const f = params.get("filter");
    return f && CAPABILITY_FILTERS.some((c) => c.key === f) ? [f] : [];
  });
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState([]);
  const categoryId = params.get("category_id") || "";
  const sort = params.get("sort") || "rating";

  const load = useCallback(async () => {
    if (!tenant) return;
    setLoading(true);
    const fetchers = {
      categories: () => getCategories(),
      offers: () => api.get("/offers", { params: { tenant_id: tenant.id } }),
      businesses: () =>
        api.get("/directory/businesses", {
          params: {
            tenant_id: tenant.id,
            q: params.get("q") || undefined,
            category_id: categoryId || undefined,
            sort: sort === "rating" ? undefined : sort,
          },
        }),
    };
    const keys = Object.keys(fetchers);
    const setters = { categories: setCategories, offers: setOffers, businesses: setBusinesses };
    const results = await Promise.allSettled(keys.map((k) => fetchers[k]()));
    const nextFailed = [];
    results.forEach((res, i) => {
      const key = keys[i];
      if (res.status === "fulfilled") setters[key](res.value.data);
      else nextFailed.push(key);
    });
    setFailed(nextFailed);
    setLoading(false);
  }, [tenant, params, categoryId, sort, getCategories]);

  useEffect(() => {
    load();
  }, [load]);

  // `?category=<slug>` (e.g. the home "Book a doctor" card → `hospital`): category ids differ per
  // environment but the national category slugs don't, so a link can name the slug and this turns
  // it into the `category_id` the list is filtered by, once the categories have loaded.
  useEffect(() => {
    const slug = params.get("category");
    if (!slug || categoryId || !categories.length) return;
    const match = categories.find((c) => c.slug === slug);
    const next = new URLSearchParams(params);
    next.delete("category");
    if (match) next.set("category_id", match.id);
    setParams(next, { replace: true });
  }, [params, categoryId, categories, setParams]);

  const applySearch = () => {
    const next = new URLSearchParams(params);
    if (query) next.set("q", query);
    else next.delete("q");
    setParams(next);
  };

  const setCategory = (id) => {
    const next = new URLSearchParams(params);
    if (id) next.set("category_id", id);
    else next.delete("category_id");
    setParams(next);
  };

  const setSort = (value) => {
    const next = new URLSearchParams(params);
    if (value && value !== "rating") next.set("sort", value);
    else next.delete("sort");
    setParams(next);
  };

  const toggleCapability = (key) => {
    setActiveCapabilities((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const visibleBusinesses = businesses.filter((b) =>
    activeCapabilities.every((key) => CAPABILITY_FILTERS.find((f) => f.key === key)?.match(b))
  );

  const shownCategories = categories.filter((c) => c.listing_count !== 0);
  // Nothing has been searched or filtered: an empty list means the city has no listings yet, so
  // say that (and invite the first one) instead of "try a different search".
  const hasNarrowing = Boolean(params.get("q") || categoryId || activeCapabilities.length);
  const cityIsEmpty = !loading && failed.length === 0 && visibleBusinesses.length === 0 && !hasNarrowing;

  return (
    <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="font-heading text-2xl font-bold tracking-tight">{t("directory.title")}</h1>
        {/* U9: no count while the first fetch is in flight - "0 listings" would be a lie. */}
        {!loading && !cityIsEmpty && (
          <p className="text-sm font-medium text-muted-foreground" data-testid="directory-result-count">
            {t("directory.resultCount", { count: visibleBusinesses.length })}
          </p>
        )}
      </div>

      <div className="mt-6 flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex max-w-xl flex-1 items-center gap-2 rounded-full border border-border bg-card p-2">
            <Search size={18} className="ml-3 text-muted-foreground" />
            <Input
              data-testid="directory-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applySearch()}
              placeholder={t("common.searchPlaceholder")}
              className="border-0 bg-transparent shadow-none focus-visible:ring-0"
            />
            <Button className="rounded-full px-5" data-testid="directory-search-btn" onClick={applySearch}>
              {t("common.search")}
            </Button>
          </div>

          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-40 rounded-full" data-testid="directory-sort-select">
              <SelectValue placeholder={t("directory.sortLabel")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="rating" data-testid="directory-sort-rating">
                {t("directory.sortRating")}
              </SelectItem>
              <SelectItem value="newest" data-testid="directory-sort-newest">
                {t("directory.sortNewest")}
              </SelectItem>
              <SelectItem value="name" data-testid="directory-sort-name">
                {t("directory.sortName")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {shownCategories.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="category-filters">
          <button
            onClick={() => setCategory("")}
            data-testid="category-filter-all"
            className={`rounded-full border px-4 py-1.5 text-sm transition-colors duration-200 ${
              !categoryId ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground" : "border-border"
            }`}
          >
            {t("common.all")}
          </button>
          {/* RF-11: a category with no live listing in this city is a dead filter. */}
          {shownCategories.map((c) => (
            <button
              key={c.id}
              onClick={() => setCategory(c.id)}
              data-testid={`category-filter-${c.slug}`}
              className={`rounded-full border px-4 py-1.5 text-sm transition-colors duration-200 ${
                categoryId === c.id ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground" : "border-border"
              }`}
            >
              {localized(lang, c, "name")}
            </button>
          ))}
        </div>
        )}

        <div className="flex flex-wrap gap-2" data-testid="capability-filters">
          {CAPABILITY_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => toggleCapability(f.key)}
              data-testid={`capability-filter-${f.key}`}
              className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors duration-200 ${
                activeCapabilities.includes(f.key)
                  ? "border-transparent bg-[hsl(var(--accent))] text-accent-foreground"
                  : "border-border bg-card text-muted-foreground"
              }`}
            >
              {t(f.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {failed.length > 0 && (
        <div
          className="mt-6 flex flex-wrap items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
          data-testid="directory-error"
        >
          {t("directory.couldntLoad")}
          <Button size="sm" variant="outline" className="rounded-full" data-testid="directory-retry" onClick={load}>
            {t("common.retry")}
          </Button>
        </div>
      )}

      {loading ? (
        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="directory-loading">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-64 rounded-2xl" />
          ))}
        </div>
      ) : cityIsEmpty ? (
        <div className="mt-6 rounded-2xl border border-dashed border-border p-10 text-center" data-testid="directory-empty-city">
          <p className="text-muted-foreground">{t("directory.emptyCity")}</p>
          <Link
            to="/onboarding-request"
            data-testid="directory-list-my-shop"
            className="mt-4 inline-block rounded-full bg-[hsl(var(--accent))] px-5 py-2 text-sm font-semibold text-accent-foreground"
          >
            {t("home.listMyShop")}
          </Link>
        </div>
      ) : visibleBusinesses.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-border p-10 text-center" data-testid="directory-empty">
          <p className="text-muted-foreground">{t("common.noResults")}</p>
          {/* A search with no hits is the moment a missing shop owner is looking at the page. */}
          <Link
            to="/onboarding-request"
            data-testid="directory-list-my-shop"
            className="mt-4 inline-block rounded-full bg-[hsl(var(--accent))] px-5 py-2 text-sm font-semibold text-accent-foreground"
          >
            {t("home.listMyShop")}
          </Link>
        </div>
      ) : (
        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {visibleBusinesses.map((b) => (
            <BusinessCard key={b.id} business={b} offer={offers.find((o) => o.business_id === b.id)} />
          ))}
        </div>
      )}
    </div>
  );
}
